import { Schema } from "effect";

import {
  DashboardAdHocDataset,
  DashboardParameter,
  DashboardWidgetPatch,
  DashboardWidgetPatchKey,
  DashboardWidgetV2,
} from "./dashboardArtifacts";
import {
  Identifier,
  Label,
  NonNegativeCount,
  PositiveCount,
  Span,
} from "./dashboardSchemaPrimitives";

/**
 * Small, typed edits to a v2 dashboard spec. One set of operations powers
 * direct edits, the assistant, undo, version history and the change summary
 * on each version. `@t3tools/shared/dashboardEdits` applies, inverts and
 * summarizes them.
 *
 * Layout semantics: rows hold widgets. Removing or moving a row's last widget
 * deletes the row. A position is read after the widget has been taken out of
 * its old place (and an emptied row deleted); with `newRow`, a new row is
 * inserted at `row` holding just the widget, otherwise the widget is inserted
 * into row `row` at `index`.
 */

export const DASHBOARD_EDIT_LIMITS = { ops: 100 } as const;

export const DashboardWidgetPosition = Schema.Struct({
  row: NonNegativeCount,
  index: NonNegativeCount,
});
export type DashboardWidgetPosition = typeof DashboardWidgetPosition.Type;

export const DashboardNewRow = Schema.Struct({ title: Schema.optionalKey(Label) });
export type DashboardNewRow = typeof DashboardNewRow.Type;

export const AddWidgetOperation = Schema.Struct({
  op: Schema.Literal("add_widget"),
  widget: DashboardWidgetV2,
  at: DashboardWidgetPosition,
  newRow: Schema.optionalKey(DashboardNewRow),
});

/** Sets the keys in `set` and removes the keys in `unset`. */
export const UpdateWidgetOperation = Schema.Struct({
  op: Schema.Literal("update_widget"),
  widgetId: Identifier,
  set: DashboardWidgetPatch,
  unset: Schema.optionalKey(Schema.Array(DashboardWidgetPatchKey)),
});

export const RemoveWidgetOperation = Schema.Struct({
  op: Schema.Literal("remove_widget"),
  widgetId: Identifier,
});

export const MoveWidgetOperation = Schema.Struct({
  op: Schema.Literal("move_widget"),
  widgetId: Identifier,
  to: DashboardWidgetPosition,
  newRow: Schema.optionalKey(DashboardNewRow),
});

/** `span: null` shares the row equally again. */
export const ResizeWidgetOperation = Schema.Struct({
  op: Schema.Literal("resize_widget"),
  widgetId: Identifier,
  span: Schema.NullOr(Span),
});

export const SetTitleOperation = Schema.Struct({
  op: Schema.Literal("set_title"),
  title: Label,
});

/** `description: null` removes it. */
export const SetDescriptionOperation = Schema.Struct({
  op: Schema.Literal("set_description"),
  description: Schema.NullOr(Schema.String.check(Schema.isMaxLength(1_000))),
});

/** Inserts at `index` (default: last). */
export const AddParameterOperation = Schema.Struct({
  op: Schema.Literal("add_parameter"),
  parameter: DashboardParameter,
  index: Schema.optionalKey(NonNegativeCount),
});

/** Replaces the parameter with the same id. */
export const UpdateParameterOperation = Schema.Struct({
  op: Schema.Literal("update_parameter"),
  parameter: DashboardParameter,
});

export const RemoveParameterOperation = Schema.Struct({
  op: Schema.Literal("remove_parameter"),
  parameterId: Identifier,
});

/** Inserts at `index` (default: last). */
export const AddDatasetOperation = Schema.Struct({
  op: Schema.Literal("add_dataset"),
  dataset: DashboardAdHocDataset,
  index: Schema.optionalKey(NonNegativeCount),
});

/** Replaces the dataset with the same id. */
export const UpdateDatasetOperation = Schema.Struct({
  op: Schema.Literal("update_dataset"),
  dataset: DashboardAdHocDataset,
});

export const RemoveDatasetOperation = Schema.Struct({
  op: Schema.Literal("remove_dataset"),
  datasetId: Identifier,
});

export const DashboardEditOperation = Schema.Union([
  AddWidgetOperation,
  UpdateWidgetOperation,
  RemoveWidgetOperation,
  MoveWidgetOperation,
  ResizeWidgetOperation,
  SetTitleOperation,
  SetDescriptionOperation,
  AddParameterOperation,
  UpdateParameterOperation,
  RemoveParameterOperation,
  AddDatasetOperation,
  UpdateDatasetOperation,
  RemoveDatasetOperation,
]);
export type DashboardEditOperation = typeof DashboardEditOperation.Type;
export type DashboardEditOperationKind = DashboardEditOperation["op"];

export const DASHBOARD_EDIT_OPERATION_KINDS = [
  "add_widget",
  "update_widget",
  "remove_widget",
  "move_widget",
  "resize_widget",
  "set_title",
  "set_description",
  "add_parameter",
  "update_parameter",
  "remove_parameter",
  "add_dataset",
  "update_dataset",
  "remove_dataset",
] as const satisfies ReadonlyArray<DashboardEditOperationKind>;

/** Edits made against version `baseVersion`; a stale base is rejected, not merged. */
export const DashboardEdit = Schema.Struct({
  baseVersion: PositiveCount,
  ops: Schema.Array(DashboardEditOperation).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(DASHBOARD_EDIT_LIMITS.ops),
  ),
});
export type DashboardEdit = typeof DashboardEdit.Type;

/** Where an edit failed or what it left invalid. `opIndex` is absent for whole-spec issues. */
export const DashboardEditIssue = Schema.Struct({
  opIndex: Schema.optionalKey(NonNegativeCount),
  path: Schema.Array(Schema.Union([Schema.String, Schema.Number])),
  message: Schema.String,
});
export type DashboardEditIssue = typeof DashboardEditIssue.Type;
